// services/albumService.js
// Web service layer for standalone album features
import supabase from './supabase';
import CryptoJS from 'crypto-js';
import axios from 'axios';

const CLOUD_FUNCTIONS_URL = 'https://asia-south1-invits-app.cloudfunctions.net';

/**
 * Get album by shareable_slug (for deep linking)
 * Returns album metadata with branding info
 * Does NOT require authentication for public albums
 */
export const getAlbumBySlug = async (slug) => {
  try {
    const { data: album, error } = await supabase
      .from('creator_albums')
      .select(`
        id,
        name,
        description,
        shareable_slug,
        password_hash,
        is_public,
        allow_downloads,
        watermark_enabled,
        event_id,
        event_financial_year,
        link_type,
        link_status,
        creator_id,
        studio_id,
        photos_count,
        videos_count,
        total_media_count,
        cover_photo_url,
        cover_photo_path,
        status,
        storage_expires_at
      `)
      .eq('shareable_slug', slug)
      .eq('status', 'active')
      .maybeSingle();

    // Fetch related data separately to avoid PostgREST join issues
    if (!error && album) {
      if (album.creator_id) {
        const { data: creator } = await supabase
          .from('users')
          .select('id, first_name, last_name, username, display_name, profile_photo_cdn_url')
          .eq('id', album.creator_id)
          .single();
        if (creator) album.users = creator;
      }
      if (album.studio_id) {
        const { data: studio } = await supabase
          .from('studios')
          .select('id, name, username, cover_image_cdn_url, bio')
          .eq('id', album.studio_id)
          .single();
        if (studio) album.studios = studio;
      }
    }

    if (error) throw error;
    if (!album) return null;

    return {
      ...album,
      has_password: !!album.password_hash,
      password_hash: undefined,
    };
  } catch (error) {
    console.error('Error fetching album by slug:', error);
    return null;
  }
};

/**
 * Verify album password
 * @param albumId - Album UUID
 * @param password - Plain text password to verify
 * @returns { success: boolean, verified: boolean }
 */
export const verifyAlbumPassword = async (albumId, password) => {
  try {
    const { data: album, error } = await supabase
      .from('creator_albums')
      .select('password_hash')
      .eq('id', albumId)
      .single();

    if (error) throw error;

    if (!album.password_hash) {
      return { success: true, verified: true };
    }

    const hashedPassword = CryptoJS.SHA256(password).toString();
    const verified = hashedPassword === album.password_hash;

    return { success: true, verified };
  } catch (error) {
    console.error('Error verifying album password:', error);
    return { success: false, verified: false, error: error.message };
  }
};

/**
 * Get folders for a creator album
 */
export const getAlbumFolders = async (albumId) => {
  try {
    const { data, error } = await supabase
      .from('creator_album_folders')
      .select('*')
      .eq('album_id', albumId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return { success: true, folders: data || [] };
  } catch (error) {
    console.error('Error fetching album folders:', error);
    return { success: false, error: error.message, folders: [] };
  }
};

/**
 * Get media in a specific folder (or root if folderId is null)
 */
export const getAlbumMediaInFolder = async (albumId, folderId = null) => {
  try {
    let query = supabase
      .from('creator_album_media')
      .select('*')
      .eq('album_id', albumId)
      .eq('processing_status', 'completed')
      .order('uploaded_at', { ascending: false });

    if (folderId) {
      query = query.eq('folder_id', folderId);
    }

    const { data, error } = await query;

    if (error) throw error;

    return { success: true, media: data || [] };
  } catch (error) {
    console.error('Error fetching album media:', error);
    return { success: false, error: error.message, media: [] };
  }
};

/**
 * Get album branding info (creator, studio, contributors)
 */
export const getAlbumBrandingInfo = async (albumId) => {
  try {
    const { data: album, error: albumError } = await supabase
      .from('creator_albums')
      .select('id, name, description, creator_id, studio_id')
      .eq('id', albumId)
      .single();

    if (albumError) throw albumError;

    // Fetch creator and studio info separately
    if (album?.creator_id) {
      const { data: creator } = await supabase
        .from('users')
        .select('id, first_name, last_name, username, display_name, profile_photo_cdn_url')
        .eq('id', album.creator_id)
        .single();
      if (creator) album.users = creator;
    }
    if (album?.studio_id) {
      const { data: studio } = await supabase
        .from('studios')
        .select('id, name, username, cover_image_cdn_url, bio')
        .eq('id', album.studio_id)
        .single();
      if (studio) album.studios = studio;
    }

    // Fetch contributors
    let contributors = [];
    const { data: contribData, error: contributorsError } = await supabase
      .from('album_contributors')
      .select('contributor_user_id, contributor_type')
      .eq('album_id', albumId);

    if (!contributorsError && contribData?.length) {
      const userIds = contribData.map(c => c.contributor_user_id);
      const { data: contribUsers } = await supabase
        .from('users')
        .select('id, first_name, last_name, username, display_name, profile_photo_cdn_url')
        .in('id', userIds);

      contributors = contribData.map(c => ({
        ...c,
        users: contribUsers?.find(u => u.id === c.contributor_user_id) || null,
      }));
    }

    return {
      success: true,
      album,
      contributors: contributors || [],
    };
  } catch (error) {
    console.error('Error fetching album branding info:', error);
    return { success: false, error: error.message, album: null, contributors: [] };
  }
};

/**
 * Get presigned video URL for streaming
 */
export const getPresignedVideoUrl = async (mediaId) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();

    const headers = { 'Content-Type': 'application/json' };
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    }

    const response = await axios.post(
      `${CLOUD_FUNCTIONS_URL}/getPresignedMediaUrl`,
      { mediaId, mediaType: 'album' },
      { headers, timeout: 10000 }
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to get streaming URL');
    }

    return {
      success: true,
      url: response.data.presignedUrl,
      expiresAt: response.data.expiresAt,
    };
  } catch (error) {
    console.error('Error getting presigned video URL:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Check if a slug belongs to an event (event_slugs table)
 * Returns event info if found, null otherwise
 */
export const checkEventSlug = async (slug) => {
  try {
    const { data, error } = await supabase
      .from('event_slugs')
      .select('slug, event_id, financial_year')
      .eq('slug', slug)
      .maybeSingle();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error checking event slug:', error);
    return null;
  }
};

/**
 * Download a file from URL (triggers browser download)
 */
export const downloadFile = async (url, filename) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    return true;
  } catch (error) {
    console.error('Error downloading file:', error);
    return false;
  }
};
